import type { AnswerJson } from "../questions/answerJson";
import type { GridJsonResponse } from "../questions/specify/types";
import { formatCopy, type SurveyCopy } from "../theme/copy";
import { defaultThemeV2 } from "../theme/defaultThemeV2";
import type { Question, QuestionReference } from "../util/types";
import { clearImageBlobUrlCache, prepareSurveyQuestionImages } from "./images";

export type SurveyAnswer = {
  type: string;
  value: string | number;
  responses?: string;
  responseJson?: GridJsonResponse;
  answerJson?: AnswerJson;
};

export type SurveyGroupResponse = string | AnswerJson | GridJsonResponse;

export const groupResponseFromAnswer = (
  answer: SurveyAnswer,
): SurveyGroupResponse => {
  if (answer.answerJson !== undefined) return answer.answerJson;
  if (answer.responseJson !== undefined) return answer.responseJson;
  if (answer.responses !== undefined) return answer.responses.split("\t");
  return String(answer.value);
};

export type DialSample = {
  value: number | "";
  offsetSeconds: number;
  postData: string;
  last?: boolean;
};

export type SurveyCommand =
  | { kind: "load" }
  | { kind: "answer"; answer: SurveyAnswer; sequenceId?: number }
  | {
      kind: "group";
      groupId: string;
      responses: readonly SurveyGroupResponse[];
      sequenceId: number;
    }
  | { kind: "default"; sequenceId?: number }
  | { kind: "undo"; responseIndex: number };

export type SurveyTransport = {
  request: (
    command: SurveyCommand,
    signal: AbortSignal,
  ) => Promise<Question | null>;
  storeDialSample?: (sample: DialSample, signal: AbortSignal) => Promise<void>;
  submitComment?: (
    comment: string,
    sourceId: string,
    questionReference: QuestionReference | null,
    signal: AbortSignal,
  ) => Promise<void>;
};

export class SurveyHttpError extends Error {
  constructor(
    readonly status: number,
    message = formatCopy(defaultThemeV2.copy.errors.httpStatus, { status }),
  ) {
    super(message);
  }
}

const readHttpErrorMessage = async (
  response: Response,
  errors: SurveyCopy["errors"],
): Promise<string> => {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === "string" && body.message.trim()) {
      return body.message;
    }
  } catch {
    // Responses without a JSON body use the status-based fallback below.
  }
  return formatCopy(errors.httpStatus, { status: response.status });
};

const setAnswerParams = (params: URLSearchParams, answer: SurveyAnswer) => {
  if (answer.answerJson !== undefined) {
    params.set("answer_json", JSON.stringify(answer.answerJson));
  } else if (answer.responseJson !== undefined) {
    params.set("response_json", JSON.stringify(answer.responseJson));
  } else if (answer.responses !== undefined) {
    params.set("responses", answer.responses);
  } else {
    params.set("response", String(answer.value));
  }
};

export const buildSurveyUiUrl = (
  apiOrigin: URL,
  surveyId: string,
  command: SurveyCommand,
): URL => {
  const url = new URL(apiOrigin);
  url.pathname = `/v1/ui/${surveyId}`;
  const params = new URLSearchParams();

  if (command.kind === "default") {
    params.set("submit_default_response", "true");
  } else if (command.kind === "undo") {
    params.set("undo", String(command.responseIndex));
  } else if (command.kind === "answer") {
    setAnswerParams(params, command.answer);
  }

  if (
    (command.kind === "answer" || command.kind === "default") &&
    command.sequenceId !== undefined
  ) {
    params.set("sequence_id", String(command.sequenceId));
  }

  url.search = params.toString();
  return url;
};

export class HttpSurveyTransport implements SurveyTransport {
  private readonly apiOrigin: URL;

  constructor(
    private readonly surveyId: string,
    apiDomain: string,
    protocol = "https",
    private readonly errors: SurveyCopy["errors"] = defaultThemeV2.copy.errors,
  ) {
    if (
      !apiDomain ||
      apiDomain.includes("/") ||
      apiDomain.includes("\\") ||
      apiDomain.includes("@") ||
      apiDomain.includes("?") ||
      apiDomain.includes("#")
    ) {
      throw new Error("Invalid survey API domain configuration");
    }

    const origin = new URL(`${protocol.replace(/:$/, "")}://${apiDomain}`);
    if (
      !["http:", "https:"].includes(origin.protocol) ||
      (import.meta.env.PROD && origin.protocol !== "https:")
    ) {
      throw new Error("Invalid survey API protocol configuration");
    }
    this.apiOrigin = origin;
  }

  async request(
    command: SurveyCommand,
    signal: AbortSignal,
  ): Promise<Question | null> {
    if (command.kind === "load") clearImageBlobUrlCache();

    const url = buildSurveyUiUrl(this.apiOrigin, this.surveyId, command);
    const requestInit: RequestInit = {
      credentials: "include",
      signal,
      ...(command.kind === "group"
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              group_id: command.groupId,
              responses: command.responses,
              sequence_id: command.sequenceId,
            }),
          }
        : {}),
    };
    // The request target inherits the validated, configured API origin.
    // nosemgrep
    const response = await fetch(url, requestInit);
    if (!response.ok) {
      throw new SurveyHttpError(
        response.status,
        await readHttpErrorMessage(response, this.errors),
      );
    }

    const question = (await response.json()) as Question | null;
    if (!question) return null;

    const prepared = await prepareSurveyQuestionImages(
      question,
      this.apiOrigin,
    );
    if (prepared.question.type === "complete") clearImageBlobUrlCache();
    return prepared;
  }

  async storeDialSample(
    sample: DialSample,
    signal: AbortSignal,
  ): Promise<void> {
    const url = new URL(this.apiOrigin);
    url.pathname = `/v1/store/${this.surveyId}`;
    url.search = new URLSearchParams({
      value: String(sample.value),
      offset_seconds: String(sample.offsetSeconds),
      ...(sample.last ? { last: "true" } : {}),
    }).toString();
    // The request target inherits the validated, configured API origin.
    // nosemgrep
    const response = await fetch(url, {
      method: "POST",
      body: sample.postData,
      credentials: "include",
      signal,
    });
    if (!response.ok) {
      throw new SurveyHttpError(
        response.status,
        await readHttpErrorMessage(response, this.errors),
      );
    }
  }

  async loginVisitor(sealedResult: string): Promise<void> {
    const url = new URL(this.apiOrigin);
    url.pathname = `/v1/login/${this.surveyId}`;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const response = await fetch(url, { method: "POST", body: sealedResult });
      if (response.status !== 503 || attempt === 2) return;
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    }
  }

  async submitComment(
    comment: string,
    sourceId: string,
    questionReference: QuestionReference | null,
    signal: AbortSignal,
  ): Promise<void> {
    const url = new URL(this.apiOrigin);
    url.pathname = `/v1/comment/${this.surveyId}`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        comment,
        source_id: sourceId,
        question_reference: questionReference,
      }),
      signal,
    });
    if (!response.ok) {
      throw new SurveyHttpError(
        response.status,
        await readHttpErrorMessage(response, this.errors),
      );
    }
  }
}

export const createBrowserSurveyTransport = (
  copy: SurveyCopy = defaultThemeV2.copy,
): HttpSurveyTransport =>
  new HttpSurveyTransport(
    window.location.pathname.split("/").filter(Boolean)[0] ?? "",
    import.meta.env.VITE_MX8_SURVEY_API_DOMAIN as string,
    (import.meta.env.VITE_MX8_SURVEY_API_PROTOCOL as string | undefined) ??
      "https",
    copy.errors,
  );
